import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { parseAsString, useQueryState } from 'nuqs'

import getErrorBoundary from '~publish/components/ErrorBoundary'
import { lazyPage } from '~publish/helpers/lazyPage'

import { ChangelogAnnouncementModal } from './ChangelogAnnouncementModal'
import { ChangelogNotificationCard } from './ChangelogNotificationCard'
import {
  useChangelogEntryToDisplay,
  useMarkChangelogEntrySeen,
} from './useChangelog'
import type { ChangelogEntryToDisplay } from './useChangelog'
import { useChangelogTracking } from './useChangelogTracking'

import styles from './ChangelogNotification.module.css'

import type { JSX } from 'react'

// Deferred: the dialog pulls in `platejs` for `ChangelogEntryEditor`, only
// needed once someone actually opens "What's New" — the bell/card above
// stays a normal import so it's never delayed.
const ChangelogDialog = lazyPage(
  () => import('./ChangelogDialog'),
  'ChangelogDialog',
)
const ChangelogErrorBoundary = getErrorBoundary()

/**
 * TEMPORARY: Extend Window interface for app-shell integration.
 * This is a workaround until the new IA navigation is complete,
 * which will have proper React Router context access.
 */
declare global {
  interface Window {
    __openChangelog?: () => void
  }
}

/**
 * Floating notification card that appears at bottom-left when there's a new changelog entry.
 * Clicking the card opens the full entry dialog.
 * Dismissed entries are tracked server-side via markChangelogEntryAsSeen mutation.
 *
 * URL state:
 * - No ?changelog param: dialog closed
 * - ?changelog (empty): dialog open, list view
 * - ?changelog=<id>: dialog open, specific entry view
 */
export function ChangelogNotification(): JSX.Element | null {
  const location = useLocation()
  const isSettingsPage = location.pathname.startsWith('/settings')
  const { entry, loading } = useChangelogEntryToDisplay()
  const markSeen = useMarkChangelogEntrySeen()
  const { trackChangelogEntryDismissed, trackChangelogEntryCtaClicked } =
    useChangelogTracking()
  // URL is the source of truth for dialog state
  // null = no param (closed), "" = list view, "<id>" = entry view
  const [changelogParam, setChangelogParam] = useQueryState(
    'changelog',
    parseAsString,
  )

  const isDialogOpen = changelogParam !== null
  const currentEntryId = changelogParam || undefined // "" becomes undefined (list view)

  // Latches true the first time the dialog opens (click, `?changelog` deep
  // link, or the `window.__openChangelog` escape hatch) and never resets —
  // gates the dialog's lazy import so its chunk loads on first open rather
  // than on every app mount.
  const [hasDialogEverOpened, setHasDialogEverOpened] =
    React.useState(isDialogOpen)
  React.useEffect(() => {
    if (isDialogOpen) setHasDialogEverOpened(true)
  }, [isDialogOpen])

  // Expose the changelog opener on window for callers that don't share this
  // component's nuqs state (user menu, command bar).
  React.useEffect(() => {
    window.__openChangelog = (): void => {
      void setChangelogParam('')
    }
    return (): void => {
      delete window.__openChangelog
    }
  }, [setChangelogParam])

  const handleOpenDialog = (): void => {
    if (entry) {
      void setChangelogParam(entry.id)
    }
  }

  const handleDismiss = (): void => {
    if (entry) {
      trackChangelogEntryDismissed({ id: entry.id, title: entry.title })
      markSeen(entry.id)
    }
  }

  const handleDialogOpenChange = (open: boolean): void => {
    if (!open) {
      void setChangelogParam(null)
      // Mark as seen when user closes the dialog
      if (entry) {
        markSeen(entry.id)
      }
    }
  }

  const handleEntryIdChange = (entryId: string | null): void => {
    // null = list view (empty string in URL), string = entry view
    void setChangelogParam(entryId ?? '')
  }

  // The announcement modal is *latched*: once shown, it stays mounted from this
  // captured entry until the user dismisses it, so a background query
  // re-emission (e.g. the seen-refetch, or a changed entry reference) can't
  // unmount it mid-view and flash it. The bottom-left popover card stays live —
  // it's inline, so churn there isn't visible. `dismissedModalIds` stops a
  // just-dismissed entry from re-latching before the refetch nulls it out.
  const [modalEntry, setModalEntry] =
    React.useState<ChangelogEntryToDisplay | null>(null)
  const dismissedModalIds = React.useRef<Set<string>>(new Set())

  React.useEffect(() => {
    if (
      !loading &&
      entry?.id &&
      entry.displayType === 'modal' &&
      !isDialogOpen &&
      !modalEntry &&
      !dismissedModalIds.current.has(entry.id)
    ) {
      setModalEntry(entry)
    }
    // Keyed by `entry?.id` only: the object reference changes across Apollo
    // emissions, but the latch should fire once per entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, entry?.id, isDialogOpen, modalEntry])

  const handleModalDismiss = (): void => {
    if (!modalEntry) return
    dismissedModalIds.current.add(modalEntry.id)
    trackChangelogEntryDismissed({
      id: modalEntry.id,
      title: modalEntry.title,
    })
    markSeen(modalEntry.id)
    setModalEntry(null)
  }

  const handleModalCtaClick = (
    cta: { id: string; label: string; url: string },
    opensExternally: boolean,
  ): void => {
    if (!modalEntry) return
    trackChangelogEntryCtaClicked(
      { id: modalEntry.id, title: modalEntry.title },
      cta,
      'modal',
    )
    // An in-app CTA navigates away, so mark it seen; an external CTA leaves the
    // modal open, so don't tear it down here.
    if (!opensExternally) {
      dismissedModalIds.current.add(modalEntry.id)
      markSeen(modalEntry.id)
      setModalEntry(null)
    }
  }

  // The popover card shows live (only for non-modal entries); the modal is
  // driven by the latched `modalEntry` above. Gated on `!modalEntry` so a still-
  // latched modal and the popover can't render together if the display slot
  // changes to a non-modal entry mid-view.
  const showPopoverCard =
    !modalEntry &&
    !loading &&
    entry &&
    !isDialogOpen &&
    entry.displayType !== 'modal'

  return (
    <>
      {/* Gated on `!isDialogOpen` too: the app-shell `window.__openChangelog`
          escape hatch can open the What's New dialog independently, and we don't
          want the announcement modal stacked underneath it. */}
      {modalEntry && !isDialogOpen && (
        <ChangelogAnnouncementModal
          title={modalEntry.title}
          summary={modalEntry.summary}
          coverImageUrl={
            typeof modalEntry.coverImageUrl === 'string'
              ? modalEntry.coverImageUrl
              : null
          }
          ctas={modalEntry.ctas}
          onDismiss={handleModalDismiss}
          onCtaClick={handleModalCtaClick}
        />
      )}

      {showPopoverCard && (
        <div
          className={styles.container}
          data-settings-page={isSettingsPage ? '' : undefined}
        >
          <ChangelogNotificationCard
            title={entry.title}
            coverImageUrl={
              typeof entry.coverImageUrl === 'string'
                ? entry.coverImageUrl
                : null
            }
            audience={entry.audience}
            onOpen={handleOpenDialog}
            onDismiss={handleDismiss}
          />
        </div>
      )}

      {hasDialogEverOpened && (
        <ChangelogErrorBoundary>
          <React.Suspense fallback={null}>
            <ChangelogDialog
              open={isDialogOpen}
              onOpenChange={handleDialogOpenChange}
              entryId={currentEntryId}
              onEntryIdChange={handleEntryIdChange}
            />
          </React.Suspense>
        </ChangelogErrorBoundary>
      )}
    </>
  )
}
